import { useEffect, useLayoutEffect, useState } from "react";
import EditorPreviewContainer from "../EditorPreviewContainer";
import { FlyerForm } from "../FlyerEditor";
import FlyerPreview from "../FlyerPreview";
import SvgWrappableText from "../preview/SvgWrappableText";
import useLayout from "../util/useLayout";
import SvgWidthResizeText from "../preview/SvgWidthResizeText";
import pxFromCm from "../util/pxFromCm";
import SvgText from "../preview/SvgText";
import RadioGroup from "../components/RadioGroup";
import { useTranslation } from "react-i18next";
import { useFlyerConfig } from "../util/useFlyerConfig";
import { Spacer } from "../editor/EditorHelpers";
import BackgroundPicker, { BackgroundProps } from "../components/BackgroundPicker";
import { useRestorable, v } from "../util/useRestorable";
import { PresetTextEditor } from "../components/TextEditor";
import { useIsHardNo } from "../util/useDomain";

type StarEffect = 'star-burst' | 'outline' | 'none';
type Symbol = 'star' | 'circle';

export default function StarSticker() {
  const { t } = useTranslation();
  const { flyerLocale } = useFlyerConfig();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const { isSmallScreen } = useLayout();
  const [starEffect, setStarEffect] = useState<StarEffect>('star-burst');
  const [symbol, setSymbol] = useState<Symbol>('star');
  const [bigHeader, setBigHeader] = useRestorable(dt('default.buy-from-allies').toUpperCase(), 'bigHeader');
  const [subheader, setSubheader] = useRestorable(dt('default.boycott-usa.title').toUpperCase(), 'subheader');
  const [background, setBackground] = useState<BackgroundProps>({ backgroundColor: "#002266" });

  return (
    <EditorPreviewContainer>
      <FlyerForm $isSmallScreen={isSmallScreen}>
        <PresetTextEditor 
          label={t('big-header.label')}
          value={bigHeader}
          onChange={setBigHeader}
        />
        <Spacer $height="15px"/>
        <PresetTextEditor 
          label={t('subheader.label')}
          value={subheader}
          onChange={setSubheader}
        />
        <Spacer $height="15px"/>
        <RadioGroup 
          label={t('symbol.label')}
          options={[
            {
              id: 'radio.star',
              value: 'star',
              label: t('star.option')
            },
            {
              id: 'radio.circle',
              value: 'circle',
              label: t('circle.option')
            },
          ]}
          selectedValue={symbol}
          onChange={(v) => setSymbol(v as Symbol)}
        />
        <Spacer $height="15px"/>
        <RadioGroup 
          label={t('star-effect.label')}
          options={[
            {
              id: 'radio.star-burst',
              value: 'star-burst',
              label: t('star-burst.option')
            },
            {
              id: 'radio.outline',
              value: 'outline',
              label: t('outline.option')
            },
            {
              id: 'radio.none',
              value: 'none',
              label: t('none.option')
            },
          ]}
          selectedValue={starEffect}
          onChange={(v) => setStarEffect(v as StarEffect)}
        />
        <Spacer $height="15px" />
        <BackgroundPicker 
          label={t('background.label')}
          defaultColor={background.backgroundColor}
          defaultImage={null}
          onChange={setBackground} 
        />
      </FlyerForm>
      <FlyerPreview 
        background={background} 
        getAnalyticsDetails={() => JSON.stringify({
          bigHeader,
          subheader,
          starEffect
        })} >
        <StarStickerPreview 
          starEffect={starEffect}
          bigHeader={v(bigHeader)}
          subheader={v(subheader)}
          drawCircles={symbol === 'circle'}
        />
      </FlyerPreview>
    </EditorPreviewContainer>
  )
}

interface StarStickerPreviewProps {
  starEffect: StarEffect;
  bigHeader: string;
  subheader: string;
  drawCircles: boolean;
}

const StarStickerPreview: React.FC<StarStickerPreviewProps> = ({
  starEffect, bigHeader, subheader, drawCircles,
}) => {
  const [mainTextHeight, setMainTextHeight] = useState(0);
  const [subtextHeight, setSubtextHeight] = useState(0);
  const [adjustY, setAdjustY] = useState(0);
  const [bboxes, setBBoxes] = useState([]);
  const width = 496 * 5;
  const height = 496 * 5;
  const gap = 60;
  const subtextWidth = 700;
  const mainTextTop = adjustY + 0.5 * (height - mainTextHeight - subtextHeight - (subtextHeight == 0 ? 0 : gap));
  const subtextTop = mainTextTop + mainTextHeight + gap;

  const handleMainTextLayout = (h, boxes) => {
    setMainTextHeight(h);
    setBBoxes(boxes);
  }

  const handleSubtextLayout = (ch, d) => {
    setSubtextHeight(ch + d);
  }

  // Germany 84 4.6 92 176
  // Mexico 132 1.8 36 168
  // United Kingdom 69 3.5 70 139
  // France 66 3.1 62 128
  // Turkey 87 1.3 26 113 removed
  // Italy 59 2.3 46 105
  // Canada 42 2.2 44 86
  // Spain 47 1.7 34 81
  // Australia 28 1.8 36 64
  // Poland 38 0.9 18 56
  // Ukraine 39 0.18 3.6 43
  // Netherlands 18 1.2 24 42
  const outerStars = [
    [ // Netherlands 18 1.2
      ["#9F2C2E", 1],
      ["#FFFFFF", 1],
      ["#2A4586", 1],
    ],
    [ // Germany 84 4.6
      ["#000000", 1],
      ["#CA2B1D", 1],
      ["#F7D046", 1],
    ],
    [ // Poland 38 0.9
      ["#FFFFFF", 1],
      ["#CA3142", 1],
    ],
    [ // Ukraine 39 0.18
      ["#2356B1", 1],
      ["#F8D949", 1],
    ],
    [ // Romania 19 0.4
      ["#0E2A7A", 1],
      ["#F5D34B", 1],
      ["#BD2D2F", 1],
    ],
    [ // Australia 28 1.8
      ["#0A2065", 4],
      ["#FFFFFF", 2],
      ["#D12D35", 1],
    ],
    [ // Italy 59 2.3
      ["#40904E", 1],
      ["#FFFFFF", 1],
      ["#BD3B3D", 1],
    ],
    [ // Spain 47 1.7
      ["#B52928", 2],
      ["#F6C644", 3],
      ["#B52928", 2],
    ],
    [ // Mexico 132 1.8
      ["#2B6649", 5],
      ["#FFFFFF", 2],
      ["#854A29", 1],
      ["#FFFFFF", 2],
      ["#BD2D2F", 5],
    ],
    [ // France 66 3.1
      ["#0B2551", 1],
      ["#FFFFFF", 1],
      ["#BD2D2F", 1],
    ],
    [ // Canada 42 2.2
      ["#C43C2C", 2],
      ["#FFFFFF", 1],
      ["#C43C2C", 1],
      ["#FFFFFF", 1],
      ["#C43C2C", 2],
    ],
    [ // United Kingdom 69 3.5
      ["#0A2065", 1],
      ["#FFFFFF", 1],
      ["#B72B35", 1],
    ],
  ];
  // Romania 19 0.4 8 27 promoted
  // Switzerland 9 0.9 18 27
  // Belgium 12 0.7 14 26
  // Sweden 11 0.6 12 23
  // Austria 9 0.5 10 19
  // Czechia 11 0.3 6 17
  // Ireland 5 0.6 12 17
  // Greece 10 0.3 6 16
  // Portugal 10 0.3 6 16
  // Norway 6 0.5 10 16
  // Denmark 6 0.4 8 14
  // Finland 6 0.3 6 12
  const middleStars = [
    [ // Norway 6 0.5
      ["#AA2735", 2],
      ["#FFFFFF", 1],
      ["#081F57", 1],
    ],
    [ // Sweden 11 0.6
      ["#21518E", 2],
      ["#F6CD45", 1],
    ],
    [ // Finland 6 0.3
      ["#FFFFFF", 2],
      ["#102E68", 1],
    ],//
    [ // Czechia 11 0.3
      ["#21447A", 2],
      ["#FFFFFF", 1],
      ["#C53028", 1],
    ],
    [ // Austria 9 0.5
      ["#B72B35", 1],
      ["#FFFFFF", 1],
      ["#B72B35", 1],
    ],
    [ // Bulgaria 7 0.11
      ["#FFFFFF", 1],
      ["#429471", 1],
      ["#C53925", 1],
    ],
    [ // Greece 10 0.3
      ["#2A5DA9", 1],
      ["#FFFFFF", 1],
      ["#2A5DA9", 1],
      ["#FFFFFF", 1],
      ["#2A5DA9", 1],
    ],
    [ // Portugal 10 0.3
      ["#275E39", 3],
      ["#FAEF50", 1],
      ["#D93832", 4],
    ],
    [ // Switzerland 9 0.9
      ["#C83C2B", 2],
      ["#FFFFFF", 1],
    ],
    [ // Ireland 5 0.6
      ["#489967", 1],
      ["#FFFFFF", 1],
      ["#EF8E4F", 1],
    ],
    [ // Belgium 12 0.7
      ["#000000", 1],
      ["#F7DB52", 1],
      ["#DC4647", 1],
    ],
    [ // Denmark 6 0.4
      ["#B72B35", 2],
      ["#FFFFFF", 1],
    ],
  ];
  const innerStars = [
    [ // Estland 1 0.04
      ["#3070C7", 1],
      ["#000000", 1],
      ["#FFFFFF", 1],
    ],//
    [ // Latvia 2 0.05
      ["#902D38", 3],
      ["#FFFFFF", 2],
      ["#902D38", 3],
    ],
    [ // Lithuania 3 0.08
      ["#F3BC44", 1],
      ["#2C6847", 1],
      ["#B13634", 1],
    ],
    [ // North Macedonia 2 0.015
      ["#C63631", 2],
      ["#F5E959", 1],
    ],
    [ // Albania 3 0.026
      ["#EA3323", 2],
      ["#000000", 1],
    ],
    [ // Montenegro 1 0.008
      ["#F5C242", 1],
      ["#D02C1E", 3],
      ["#F5C242", 1],
    ],
    [ // Bosnia and Herzegovina 3 0.028
      ["#0A228F", 4],
      ["#FFFFFF", 1],
      ["#F6CD45", 2],
    ],
    [ // Croatia 4 0.09
      ["#EA3323", 1],
      ["#FFFFFF", 1],
      ["#171790", 1],
    ],
    [ // Slovenia 2 0.07
      ["#FFFFFF", 4],
      ["#FFFF54", 1],
      ["#0000ED", 5],
      ["#EA3323", 5],
    ],
    [ // Moldova 3 0.018
      ["#1B45A7", 5],
      ["#F8D447", 2],
      ["#A88060", 1],
      ["#F8D447", 2],
      ["#BB2936", 5],
    ],
    [ // Luxemburg 1 0.09
      ["#DC4647", 1],
      ["#FFFFFF", 1],
      ["#48A0DB", 1],
    ],
    [ // Iceland 1 0.033
      ["#215197", 2],
      ["#FFFFFF", 1],
      ["#CA363C", 1],
    ],
  ];
  /*
    [ // Malta 1 0.024
      ["#FFFFFF", 1],
      ["#BE2E33", 1],
    ],
  */
/*
export type EuropeCountry =
  "NO" | "IS" | "DK" | "SE" | "FI" // Nordics
  | "IE" | "UK" | "BE" | "NL" | "LU" | "FR" // Western 
  | "DE" | "PL" | "CZ" | "AT" | "CH" // Central
  | "EE" | "LV" | "LT" // Baltics
  // South Eastern
  | "UA" | "MD" | "RO" | "BG" | "MK" | "AL" | "ME" 
  | "BA" // Bosnia and Herzegovina
  | "HR" // Croatia
  | "SI" // Slovenia
  | "AM" // Armenia 
  // South
  | "PT" | "ES" | "IT" | "MT" | "GR" | "TR";
*/

  const reachLimit = 680;
  const outerRingRadius = 0.5 * height - 180;
  const scalePerRing = 0.87; // 0.85;
  const itemScalePerRing = 0.87;
  const margin = pxFromCm(0.75);
  const starBurst = starEffect === 'star-burst';
  const outline = starEffect === 'outline';

  const [recursion, setRecursion] = useState(0);
  const [maxTextReach, setMaxTextReach] = useState(0);
  const [fontSize, setFontSize] = useState(260);

  useLayoutEffect(() => {
    if (!subheader) {
      setSubtextHeight(0);
    }
    setRecursion(0);
    setFontSize((prev) => {
      if (prev === 260) {
        return 255;
      }
      return 260;
    });
  }, [subheader, bigHeader])

  useLayoutEffect(() => {
    if (!bboxes || bboxes.length === 0) {
      return;
    }
    const yOffset = mainTextTop - bboxes[0].y;
    const max = bboxes.reduce((acc, bbox) => {
      const getR = (x, y) => {
        const dx = x - (0.5 * width);
        const dy = (y + yOffset) - (0.5 * height);
        return Math.sqrt(dx * dx + dy * dy);
      }
      return Math.max(
        acc,
        [
          getR(bbox.x, bbox.y),
          getR(bbox.x, bbox.y + bbox.height),
          getR(bbox.x + bbox.width, bbox.y),
          getR(bbox.x + bbox.width, bbox.y + bbox.height),
        ].reduce((acc, v) => Math.max(acc, v), 0)
      );
    }, 0)
    setMaxTextReach(max);
    const w0 = bboxes[0].width;
    const w1 = bboxes[bboxes.length - 1].width;
    if (w1 > 0) {
      const ratio = w0 / w1;
      const maxAdjust = 60;
      if (ratio > 2) {
        setAdjustY(maxAdjust);
      } else if (ratio < 0.5) {
        setAdjustY(-maxAdjust);
      } else if (ratio < 1) {
        const f = (1 / ratio) - 1;
        setAdjustY(-f * maxAdjust);
      } else {
        const f = ratio - 1;
        setAdjustY(f * maxAdjust);
      }
    }
  }, [bboxes]);

  useEffect(() => {
    if (maxTextReach < 1 || recursion >= 20) {
      return;
    }
    if (maxTextReach > reachLimit) {
      setTimeout(() => {
        setFontSize(fontSize - 10);
        setRecursion(recursion + 1);  
      }, 5);
    }
  }, [maxTextReach])

  const { isHardNo } = useIsHardNo();

  return (
    <svg width="100%" height="100%" className="has-custom-font">
      <SvgWrappableText
        text={bigHeader}
        color={"#FFFFFF"}
        x={width / 2}
        top={mainTextTop}
        fontFamily='Helvetica, Arial, sans-serif'
        fontSize={fontSize}
        fontWeight="600"
        width={reachLimit * 1.4}
        textAnchor="middle"
        onLayout={handleMainTextLayout}
      />
      {false && <circle
        cx={0.5 * width}
        cy={0.5 * height}
        r={reachLimit} 
        fill="none"
        stroke="#5500ee"
        strokeWidth={12}
      />}
      {false && bboxes.map((bbox) => {
        if (!bbox) {
          return (          <rect 
            x={400}
            y={400}
            width={800}
            height={800}
            fill="none"
            stroke="black"
            strokeWidth={8}
          />)
        }
        const yOffset = mainTextTop - bboxes[0].y;
        return (
          <rect 
            x={bbox.x}
            y={bbox.y + yOffset}
            width={bbox.width}
            height={bbox.height}
            fill="none"
            stroke="#ee00dd"
            strokeWidth={12}
          />
        );
      })}
      {subheader && <SvgWidthResizeText 
        text={subheader}
        color={"#FFFFFF"}
        x={0.5 * (width - subtextWidth)}
        capTop={subtextTop}
        width={subtextWidth}
        maxFontSize={120}
        onLayout={handleSubtextLayout}
        hasEffect={false}
      />}
      {<SvgText 
        text={isHardNo ? "hardno.eu" : "boycott-flyer.eu"}
        color={"rgba(255, 255, 255, 0.5"}
        x={width - margin}
        y={height - margin}
        textAnchor="end"
        fontSize={40}
        fontWeight="600"
        hasEffect={false}
      />}
      {outerStars.map((sc, index) => {
        const a = index * (360 / 12);
        const arad = a * Math.PI / 180;
        const r = outerRingRadius; 
        return (<SvgLayeredStar 
          key={index}
          cx={0.5 * width + r * Math.sin(arad)}
          cy={0.5 * height - r * Math.cos(arad)}
          r={120}
          sc={sc}
          starBurst={starBurst}
          outline={outline}
          drawCircles={drawCircles}
        />)
      })}
      {middleStars.map((sc, index) => {
        const a = index * (360 / 12) + 15;
        const arad = a * Math.PI / 180;
        const r = outerRingRadius * scalePerRing; 
        return (<SvgLayeredStar 
          key={index}
          cx={0.5 * width + r * Math.sin(arad)}
          cy={0.5 * height - r * Math.cos(arad)}
          r={120 * itemScalePerRing}
          sc={sc}
          starBurst={starBurst}
          outline={outline}
          drawCircles={drawCircles}
        />)
      })}
      {innerStars.map((sc, index) => {
        const a = index * (360 / 12);
        const arad = a * Math.PI / 180;
        const r = outerRingRadius * scalePerRing * scalePerRing; 
        return (<SvgLayeredStar 
          key={index}
          cx={0.5 * width + r * Math.sin(arad)}
          cy={0.5 * height - r * Math.cos(arad)}
          r={120 * itemScalePerRing * itemScalePerRing}
          sc={sc}
          starBurst={starBurst}
          outline={outline}
          drawCircles={drawCircles}
        />)
      })}
    </svg>
  )
}

interface SvgLayeredStarProps {
  cx: number;
  cy: number;
  r: number;
  sc: (string | number)[][];
  outline?: boolean;
  starBurst?: boolean;
  startAngle?: number;
  drawCircles: boolean;
}

const SvgLayeredStar: React.FC<SvgLayeredStarProps> = ({
  cx, cy, r, sc, outline, starBurst, startAngle = 0, drawCircles
}) => {
  const hasOutline = outline || false;
  const hasStarBurst = starBurst || false;
  const calcRadius = (i, ws, r) => {
    if (i == 0) {
      return r;
    }
    const sumWeights = ws.reduce((s, v) => s + v, 0);
    const usedWeights = ws.slice(0, i).reduce((s, v) => s + v, 0);
    const proportional = (1 - usedWeights / sumWeights) * r;
    const squareBased = ws.slice(0, i).reduce((rAndUsed, w) => {      
      const r = rAndUsed[0]
      const usedWeights = rAndUsed[1];
      const remWeights = sumWeights - usedWeights;
      const newR = Math.sqrt((remWeights - w) * r * r / remWeights);
      const newUsed = usedWeights + w;
      return [newR, newUsed];
    }, [r, 0])[0];
    return 0.5 * (proportional + squareBased);
  }

  return (
    <g>
      {hasOutline && !drawCircles && <SvgStar
        cx={cx}
        cy={cy}
        r={r * 1.1}
        fill={'rgba(255, 255, 255, 0.5'}
      />}
      {hasOutline && drawCircles && <circle
        cx={cx}
        cy={cy}
        r={r * 0.75 * 1.1}
        fill={'rgba(255, 255, 255, 0.5'}
      />}
      {hasStarBurst && <SvgStar
        cx={cx}
        cy={cy}
        r={r * 1.25}
        armCount={20}
        ratio={0.3}
        startAngle={startAngle + 360 / 40}
        fill={'rgba(255, 255, 255, 0.5'}
      />}
      {sc.map((c, i) => {
        const ir = calcRadius(i, sc.map((c) => c[1]), r)
        if (drawCircles) {
          return (<circle
            cx={cx}
            cy={cy}
            r={ir * 0.75}
            fill={c[0].toString()}
          />)
        }
        return (
          <SvgStar
            key={i}
            cx={cx}
            cy={cy}
            r={ir}
            startAngle={startAngle}
            fill={c[0].toString()}
          />
        )
      })}
    </g>

  )
}

interface SvgStarProps {
  cx: number;
  cy: number;
  r: number;
  fill: string;
  armCount?: number;
  ratio?: number;
  startAngle?: number;
}

const SvgStar: React.FC<SvgStarProps> = ({
  cx, cy, r, fill, armCount, ratio, startAngle
}) => {
  const coords: string[] = [];
  armCount = armCount || 5;
  ratio = ratio || 0.382;
  startAngle = startAngle || 0;
  for (let a = 0; a < 360; a += 360 / armCount) {
    const arad0 = (startAngle + a) * Math.PI / 180;
    const arad1 = (startAngle + a + 360 / armCount / 2) * Math.PI / 180;
    const x0 = cx + r * Math.sin(arad0);
    const y0 = cy - r * Math.cos(arad0);
    coords.push(`${x0.toFixed(1)} ${y0.toFixed(1)}`)
    const x1 = cx + ratio * r * Math.sin(arad1);
    const y1 = cy - ratio * r * Math.cos(arad1);
    coords.push(`${x1.toFixed(1)} ${y1.toFixed(1)}`)
  }
  const d = "M" + coords.join(" L") + "Z";
  return (<path 
    fill={fill}
    d={d}
  />)
};